import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AccountExpanded } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { AccountDetails } from './AccountDetails.jsx'
import { AccountsList } from './AccountsList.jsx'

const AccountsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedAccount, setSelectedAccount] =
    useState<AccountExpanded | null>(null)
  const [search, setSearch] = useState(searchParams.get('search') || '')

  const {
    items: accounts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<AccountExpanded>('accounts', {
    search,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    setSearchParams(params, { replace: true })
  }, [search, setSearchParams])

  const handleAccountSelect = useCallback((account: AccountExpanded) => {
    setSelectedAccount(account)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedAccount(null)
  }, [])

  return (
    <>
      <AdminListLayout
        title="Accounts"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No accounts found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <FilterInput
                label="Search accounts"
                value={search}
                onChange={setSearch}
                placeholder="Search by account name..."
              />
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          accounts.length > 0 ? (
            <>
              <AccountsList
                accounts={accounts}
                selectedId={selectedAccount?._id}
                onSelect={handleAccountSelect}
              />
              {/* Sentinel element for infinite scroll */}
              <div ref={sentinelRef} className="h-4" />
              {/* Loading indicator */}
              {isFetchingNextPage ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md" />
                </div>
              ) : null}
            </>
          ) : null
        }
      />
      <Slider
        isOpen={!!selectedAccount}
        onClose={handleCloseDetails}
        title="Account Details"
        width="30vw"
      >
        <AccountDetails account={selectedAccount} />
      </Slider>
    </>
  )
}

export { AccountsView }
